import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock } from 'lucide-react';
import { eventsApi } from '@/features/events/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { dateInputToIsoStart, dateInputToIsoEnd } from '@/features/project/planner/dates';
import { DatePicker } from '@/components/ui/DatePicker';
import { toast } from '@/components/ui/Toast';
import type { XkEventGroup } from '@/lib/api/types';

/**
 * Create-project form body, decoupled from any modal shell so it can render
 * inside the right-panel form host (see FormPanelHost). A project maps to an
 * `xk-event-group`. On success it invalidates the brand's groups and calls
 * `onCreated`; the host owns the surrounding header/close chrome.
 */
export function ProjectForm({
  brandId,
  onCreated,
  onCancel,
  onQuotaExceeded,
}: {
  brandId: number;
  onCreated: (g: XkEventGroup) => void;
  onCancel: () => void;
  /** Fired when the backend rejects with PLAN_LIMIT_EXCEEDED. */
  onQuotaExceeded?: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [codeRef, setCodeRef] = useState('');
  const [startLocal, setStartLocal] = useState('');
  const [endLocal, setEndLocal] = useState('');

  const create = useMutation({
    mutationFn: () =>
      eventsApi.createGroup({
        name: name.trim(),
        codeRef: codeRef.trim() || null,
        companyId: brandId,
        startDate: dateInputToIsoStart(startLocal),
        endDate: dateInputToIsoEnd(endLocal),
      }),
    onSuccess: (g) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eventGroups.byBrand(brandId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.eventGroups.all });
      toast({ tone: 'success', title: `Project "${g.name}" created` });
      onCreated(g);
    },
    onError: (e: Error) => {
      const payload = (e as { payload?: { code?: string } }).payload;
      if (payload?.code === 'PLAN_LIMIT_EXCEEDED' && onQuotaExceeded) {
        onQuotaExceeded();
        return;
      }
      toast({ tone: 'error', title: 'Could not create project', description: e.message });
    },
  });

  const canSubmit = name.trim().length >= 2 && !create.isPending;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const startIso = dateInputToIsoStart(startLocal);
    const endIso = dateInputToIsoEnd(endLocal);
    if (startIso && endIso && new Date(endIso).getTime() <= new Date(startIso).getTime()) {
      toast({ tone: 'error', title: 'End date must be on or after the start date' });
      return;
    }
    create.mutate();
  };

  return (
    <form onSubmit={submit} className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
      <label className="block">
        <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
          Project name <span className="text-[var(--color-accent)]">*</span>
        </div>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Spring Tour 2026"
          autoFocus
          required
          minLength={2}
          className="w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-sm text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:border-[var(--color-xk-accent)] focus:outline-none"
        />
      </label>
      <label className="block">
        <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
          Code reference
        </div>
        <input
          type="text"
          value={codeRef}
          onChange={(e) => setCodeRef(e.target.value)}
          placeholder="e.g. SPRING26"
          className="w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-sm uppercase text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:border-[var(--color-xk-accent)] focus:outline-none"
        />
      </label>
      <div className="grid grid-cols-1 gap-3">
        <label className="block">
          <div className="mb-1.5 flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
            <CalendarClock className="h-3 w-3" />
            Start date
          </div>
          <DatePicker
            mode="date"
            value={startLocal}
            onChange={setStartLocal}
            max={endLocal || undefined}
          />
        </label>
        <label className="block">
          <div className="mb-1.5 flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
            <CalendarClock className="h-3 w-3" />
            End date
          </div>
          <DatePicker
            mode="date"
            value={endLocal}
            onChange={setEndLocal}
            min={startLocal || undefined}
          />
        </label>
      </div>
      <p className="rounded-lg border border-dashed border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-[11px] text-[var(--color-xk-text-muted)]">
        <strong className="text-[var(--color-xk-lavender)]">Optional.</strong>{' '}
        The project date range governs the valid dates of its events. You can set or change it later from the project page.
      </p>
      <div className="mt-auto flex items-center justify-end gap-2 border-t border-[var(--color-xk-border)] pt-3">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-[var(--color-xk-border)] px-4 py-2 text-xs font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-5 py-2 text-xs font-extrabold text-[#0A0A0A] transition-colors hover:bg-[var(--color-xk-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {create.isPending ? 'Creating…' : 'Create project'}
        </button>
      </div>
    </form>
  );
}
